import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { Link, useNavigate } from 'react-router-dom';
import type { ReactElement } from 'react';

import { Tooltip, Typography } from 'antd';

import type { ProjectItem } from '../api/projects.client.js';
import { formatDate, getProjectStatusLabel, memberName, STATUS_TONE } from '../lib/projectMeta.js';
import { i18n } from '../../../i18n/index.js';

interface ProjectsGridProps {
  rows: ProjectItem[];
}

export function ProjectsGrid({ rows }: ProjectsGridProps): ReactElement {
  const navigate = useNavigate();
  return (
    // Spec D `.panel`: hairline-поверхность L14, строки 42px, hover — surface-2.
    <div
      className="min-h-0 flex-1 crm-checkbox-square"
      style={{
        background: 'var(--ant-color-bg-container)',
        border: '1px solid var(--ant-color-border-secondary)',
        borderRadius: 'var(--ant-border-radius-lg, 14px)',
        overflow: 'hidden',
      }}
    >
      <table className="w-full text-[13px]" style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--ant-color-text-secondary)]">
            <th className="px-3.5 py-2 font-semibold">
              {i18n.t('crm.features.features.projects.pages.projectspage.название')}
            </th>
            <th className="px-3.5 py-2 font-semibold">
              {i18n.t('crm.features.features.projects.pages.projectspage.статус')}
            </th>
            <th className="px-3.5 py-2 font-semibold">
              {i18n.t('crm.features.features.projects.pages.projectspage.владелец')}
            </th>
            <th className="px-3.5 py-2 font-semibold">
              {i18n.t('crm.features.features.projects.pages.projectspage.срок')}
            </th>
            <th className="px-3.5 py-2 text-right font-semibold">
              {i18n.t('crm.features.features.projects.pages.projectspage.задачи')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((project) => (
            <tr
              key={project.id}
              data-testid={`project-row-${project.id}`}
              className="cursor-pointer border-t border-[var(--ant-color-border-secondary)] transition-colors hover:bg-[var(--g-fill)]"
              onClick={() => navigate(`/projects/${project.id}`)}
            >
              <td className="px-3.5 py-[11px]">
                <Link
                  to={`/projects/${project.id}`}
                  className="font-medium text-[var(--ant-color-text-heading)] hover:text-[var(--ant-color-primary)]"
                  onClick={(event) => event.stopPropagation()}
                >
                  {project.name}
                </Link>
              </td>
              <td className="px-3.5 py-[11px]">
                <EntityStatusChip tone={STATUS_TONE[project.status]} style={{ marginInlineEnd: 0 }}>
                  {getProjectStatusLabel(project.status)}
                </EntityStatusChip>
              </td>
              <td className="px-3.5 py-[11px] text-[var(--ant-color-text)]">
                {memberName(project.ownerMember)}
              </td>
              <td className="px-3.5 py-[11px] text-[var(--ant-color-text-secondary)]">
                {project.dueDate ? (
                  <Tooltip title={project.dueDate}>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatDate(project.dueDate)}
                    </span>
                  </Tooltip>
                ) : (
                  <Typography.Text type="secondary">—</Typography.Text>
                )}
              </td>
              <td
                className="px-3.5 py-[11px] text-right text-[var(--ant-color-text)]"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {project.taskCount}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
